// Implements: F-UIX-008, F-UIX-010
/**
 * The login state machine of design section 5, and nothing else.
 *
 * The hook owns WHAT the answer means; the container owns the form values and
 * the components own what is on screen. That split is what lets the whole
 * second-factor decision be tested without rendering anything.
 *
 * The detection order is DD7 and it is STRICT:
 *
 *   1. `X-Second-Factor` present  -> its value decides, and the message is not
 *      read at all. The header is legible without
 *      `Access-Control-Expose-Headers` precisely because the origin is one.
 *   2. Header absent -> EXACT equality of the message with the two constants
 *      copied from `log_in.py`. Not a substring, not case-insensitive: the
 *      handover writes the refusal lowercased and unpunctuated, so a loose
 *      match would look correct while hiding the drift.
 *   3. Any other `401` is "the credentials are not valid", and it does NOT
 *      reveal the code field. Which field was wrong is not ours to tell.
 *
 * A `429` is never an error here: it is a wait with a duration (DD8). It is
 * one of the two states a keystroke cannot leave; the other is `submitting`,
 * because a request already on its way is not something an edit can undo.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { ApiError, UNKNOWN_ERROR_CODE, secondFactorHint } from "../../../shared/api/ApiError";
import {
  SECOND_FACTOR_INVALID,
  SECOND_FACTOR_REQUIRED,
} from "../../../shared/api/authMessages";
import { parseRetryAfter } from "../../../shared/api/rateLimit";
import { SESSION_SCOPES_KEY } from "../../../shared/api/queryKeys";
import type { UserSummary } from "../../../types/api";
import type { ApiFailure } from "../../../types/errors";
import { login } from "../api/authApi";
import type { LoginCredentials } from "../api/authApi";

/** The header the closed attempt bucket uses to state its wait. */
export const RETRY_AFTER_HEADER = "Retry-After";

/** One tick of the visible countdown. */
export const COUNTDOWN_INTERVAL_MS = 1000;

/**
 * Which sentence the form shows for a failure.
 *
 * Three kinds and not the raw `message`: the backend's Spanish is safe to
 * quote only for the codes the contract enumerates (F-UIX-010), and the login
 * screen deliberately says the same thing for a wrong email and a wrong
 * password.
 */
export type LoginErrorKind = "invalidCredentials" | "organizationsSuspended" | "validation" | "unexpected";

/**
 * Owner 2026-10-19: the code of the `403` a login gets when every organization of the person is
 * suspended. The backend answers it only once every factor was proven, so naming it reveals
 * nothing to anybody else; the status and the code decide, never the message.
 */
const ORGANIZATIONS_SUSPENDED_CODE = "FORBIDDEN";

export type LoginState =
  | { readonly status: "idle" }
  | { readonly status: "submitting" }
  | { readonly status: "success"; readonly user: UserSummary }
  | { readonly status: "secondFactorRequired" }
  | { readonly status: "secondFactorInvalid" }
  | { readonly status: "locked"; readonly secondsRemaining: number }
  | { readonly status: "error"; readonly kind: LoginErrorKind; readonly failure: ApiFailure };

const SECOND_FACTOR_REQUIRED_STATE: LoginState = { status: "secondFactorRequired" };
const SECOND_FACTOR_INVALID_STATE: LoginState = { status: "secondFactorInvalid" };

/** What a thrown value that is not even an `ApiError` looks like to the form. */
function unexpected(cause: unknown): LoginState {
  return {
    status: "error",
    kind: "unexpected",
    failure: { status: 0, code: UNKNOWN_ERROR_CODE, message: String(cause) },
  };
}

/**
 * Turn one failed login into one state, in the order DD7 fixes.
 *
 * Exported because the order is the decision this slice exists to make, and a
 * test that has to render a form to check it would be testing the form.
 */
export function classifyLoginFailure(cause: unknown): LoginState {
  if (!(cause instanceof ApiError)) return unexpected(cause);

  if (cause.status === 429) {
    return {
      status: "locked",
      secondsRemaining: parseRetryAfter(cause.headers.get(RETRY_AFTER_HEADER)),
    };
  }

  if (cause.status === 401) {
    // Step 1: the header decides, and the message is not consulted.
    const hint = secondFactorHint(cause);
    if (hint === "required") return SECOND_FACTOR_REQUIRED_STATE;
    if (hint === "invalid") return SECOND_FACTOR_INVALID_STATE;

    // Step 2: exact bytes, until the header ships.
    if (cause.message === SECOND_FACTOR_REQUIRED) return SECOND_FACTOR_REQUIRED_STATE;
    if (cause.message === SECOND_FACTOR_INVALID) return SECOND_FACTOR_INVALID_STATE;

    // Step 3: anything else is a refusal, and it reveals no field.
    return { status: "error", kind: "invalidCredentials", failure: cause };
  }

  if (cause.status === 403 && cause.code === ORGANIZATIONS_SUSPENDED_CODE) {
    return { status: "error", kind: "organizationsSuspended", failure: cause };
  }
  if (cause.status === 422) return { status: "error", kind: "validation", failure: cause };
  return { status: "error", kind: "unexpected", failure: cause };
}

/** Whether a state is the server asking for the second factor. */
function revealsSecondFactor(state: LoginState): boolean {
  return state.status === "secondFactorRequired" || state.status === "secondFactorInvalid";
}

export interface UseLoginResult {
  readonly state: LoginState;
  /**
   * The server has told us this account has a second factor.
   *
   * It is remembered separately from `state` because editing a field returns
   * the machine to `idle`, and the field must NOT disappear from under the
   * fingers of whoever is typing the code into it. What the machine forgets is
   * the outcome; what this remembers is a fact about the account.
   */
  readonly secondFactorRevealed: boolean;
  /**
   * Send one attempt. It never rejects, and only one runs at a time: while a
   * request is in flight a second call is IGNORED, so two concurrent
   * `POST /auth/login` never exist and no pair of responses can race.
   */
  readonly submit: (credentials: LoginCredentials) => Promise<void>;
  /** Back to `idle` — except from `locked` and `submitting`, which an edit cannot leave. */
  readonly reset: () => void;
}

/**
 * The login machine: `idle -> submitting -> one of five outcomes`.
 *
 * On success the session query is invalidated, which is how the bootstrap of
 * slice 3b learns there is somebody signed in (design section 4). Navigation
 * is NOT here: the router arrives in slice 3b and the container decides where
 * a successful login goes.
 */
export function useLogin(): UseLoginResult {
  const queryClient = useQueryClient();
  const [state, setState] = useState<LoginState>({ status: "idle" });
  const [secondFactorRevealed, setSecondFactorRevealed] = useState(false);

  // A ref and not state: `state` is only observable after a render, so two
  // calls in the same tick would both read `idle` and both leave. Preventing
  // the SECOND request is what removes the race: with one request in flight
  // there is no pair of responses that can overwrite each other.
  const inFlight = useRef(false);

  const submit = useCallback(
    async (credentials: LoginCredentials): Promise<void> => {
      if (inFlight.current) return;
      inFlight.current = true;
      setState({ status: "submitting" });
      try {
        const user = await login(credentials);
        await queryClient.invalidateQueries({ queryKey: SESSION_SCOPES_KEY });
        setState({ status: "success", user });
      } catch (cause) {
        const next = classifyLoginFailure(cause);
        // Remembered here, in the handler that learned it, rather than in an
        // effect watching the state afterwards: an effect that writes state is
        // a second render for something already known (react-hooks).
        if (revealsSecondFactor(next)) setSecondFactorRevealed(true);
        setState(next);
      } finally {
        inFlight.current = false;
      }
    },
    [queryClient],
  );

  const reset = useCallback((): void => {
    // Two states a keystroke cannot leave: `locked`, because the wait belongs
    // to the server (DD8), and `submitting`, because collapsing it to `idle`
    // would re-enable the form, hide the only sign that something is
    // happening, and invite a second attempt.
    setState((current) =>
      current.status === "locked" || current.status === "submitting"
        ? current
        : { status: "idle" },
    );
  }, []);

  useEffect(() => {
    if (state.status !== "locked") return undefined;

    // The interval is created once per entry into `locked`, not once per tick,
    // because the dependency is the STATUS and not the number.
    const timer = setInterval(() => {
      setState((current) => {
        if (current.status !== "locked") return current;
        return current.secondsRemaining <= 1
          ? { status: "idle" }
          : { status: "locked", secondsRemaining: current.secondsRemaining - 1 };
      });
    }, COUNTDOWN_INTERVAL_MS);

    return () => {
      clearInterval(timer);
    };
  }, [state.status]);

  return { state, secondFactorRevealed, submit, reset };
}
